import { useCallback, useSyncExternalStore } from 'react';

const OPEN_STORAGE_KEY = 'ai-chat-sidebar-open';
const CONTENT_STORAGE_KEY = 'app-sidebar-selected-content';
const WIDTH_STORAGE_KEY = 'app-sidebar-width';

export const SIDEBAR_DEFAULT_WIDTH = 620;
export const SIDEBAR_MIN_WIDTH = 380;
export const SIDEBAR_MAX_WIDTH = 1200;

export type SidebarContent = 'calendar' | 'sidekick';

type Listener = () => void;

interface SidebarState {
    isOpen: boolean;
    selectedContent: SidebarContent;
    sidebarWidth: number;
}

export const isSidebarContent = (value: unknown): value is SidebarContent => {
    return value === 'calendar' || value === 'sidekick';
};

export const hasStoredOpenPreference = (): boolean => {
    return localStorage.getItem(OPEN_STORAGE_KEY) !== null;
};

const getStoredContent = (): SidebarContent => {
    const saved = localStorage.getItem(CONTENT_STORAGE_KEY);
    if (isSidebarContent(saved)) {
        return saved;
    }

    return 'sidekick';
};

const getStoredWidth = (): number => {
    const saved = localStorage.getItem(WIDTH_STORAGE_KEY);
    const parsed = saved ? parseInt(saved, 10) : NaN;
    if (isNaN(parsed)) return SIDEBAR_DEFAULT_WIDTH;
    return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, parsed));
};

let state: SidebarState = (() => {
    const savedOpenState = localStorage.getItem(OPEN_STORAGE_KEY);

    return {
        isOpen: savedOpenState ? (JSON.parse(savedOpenState) as boolean) : false,
        selectedContent: getStoredContent(),
        sidebarWidth: getStoredWidth(),
    };
})();
const listeners = new Set<Listener>();

const subscribe = (listener: Listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

const getSnapshot = () => state;

const setState = (next: Partial<SidebarState>) => {
    state = {
        isOpen: next.isOpen ?? state.isOpen,
        selectedContent: isSidebarContent(next.selectedContent) ? next.selectedContent : state.selectedContent,
        sidebarWidth: next.sidebarWidth ?? state.sidebarWidth,
    };
    localStorage.setItem(OPEN_STORAGE_KEY, JSON.stringify(state.isOpen));
    localStorage.setItem(CONTENT_STORAGE_KEY, state.selectedContent);
    if (next.sidebarWidth !== undefined) {
        localStorage.setItem(WIDTH_STORAGE_KEY, String(next.sidebarWidth));
    }
    listeners.forEach((l) => l());
};

export const useAiChatSidebarState = () => {
    const snapshot = useSyncExternalStore(subscribe, getSnapshot);

    const open = useCallback((selectedContent?: SidebarContent) => {
        setState({ isOpen: true, ...(selectedContent ? { selectedContent } : {}) });
    }, []);
    const close = useCallback(() => setState({ isOpen: false }), []);
    const toggle = useCallback((selectedContent?: SidebarContent) => {
        setState({ isOpen: !state.isOpen, ...(selectedContent ? { selectedContent } : {}) });
    }, []);
    const setSelectedContent = useCallback((selectedContent: SidebarContent) => {
        setState({ selectedContent });
    }, []);
    const setWidth = useCallback((sidebarWidth: number) => {
        setState({ sidebarWidth });
    }, []);

    return {
        isOpen: snapshot.isOpen,
        selectedContent: snapshot.selectedContent,
        sidebarWidth: snapshot.sidebarWidth,
        open,
        close,
        toggle,
        setSelectedContent,
        setWidth,
    };
};
